import Person from "@/types/Person.interface"

export default {
    methods: {
        //  Creates a contact and routes to their inbox; gets triggered if searchbar had valid 10 digit number
        async createContact(searchText: string, callback: Function): Promise<void> {
            //  Create a new person
            const model = this.newPerson()

            //  Get the phone from our searchtext
            const phone = this.getContactPhone(searchText)

            //  Set the free form number to the search text
            model.phone_default.free_form_number = phone

            //  Create our person so we can message them
            const person = await this.$store.dispatch('personsPost', model)

            //  If no callback is supplied, do nothing
            if (!callback) return

            //  Else call our callback function
            return callback(person)
        },
        //  Converts our searchtext numbers into a valid phone number
        getContactPhone(searchText: string): string {
            //  Remove all non-numeric characters
            searchText = searchText.replace(/[^0-9]/g, '')

            //  If the number starts with 1, remove it
            if (searchText.charAt(0) == '1')
                searchText = searchText.substr(1, searchText.length)

            //  Return the searchtext now that it is phone-ified
            return searchText
        },
        //  Handles inserting preset fields based on searchbar text or even creating new contact if valid enough
        async handleCreate(searchText: string | null, callback: Function): Promise<void> {
            //  If we do NOT have searchtext, just pop new contact with a new person
            if (!searchText) return this.popNewContact(this.newPerson())

            //  Else, check if any letters are in the search text
            const hasLetters = /[a-z,A-Z]/g.test(searchText)

            //  Check if any numbers are present in the search text
            const hasNumbers = /[0-9]/g.test(searchText)

            //  If somehow they do not have letters or numbers, pop new contact with a blank new person
            if (!hasLetters && !hasNumbers) return this.popNewContact(this.newPerson())

            //  Else, remove invalid characters from the phone
            const phone = hasNumbers ? this.$options.filters.phoneTrim(searchText) : null

            //  Determine if the phone is valid
            const phoneIsValid = phone?.length === 10

            //  Determine if contact names are optional
            const nameIsOptional = this.$store.getters.contactNameOptional

            //  If names are optional AND we have a valid phone number, we will auto-create the contact
            if (nameIsOptional && phoneIsValid) {
                //  Check for duplicate phone numbers
                const duplicates = await this.$store.dispatch('phoneNumbersDuplicates', phone)

                //  If duplicates do NOT exist, auto-create the contact
                if (!duplicates || duplicates.length === 0) return this.createContact(searchText, callback)

                //  Else, return the duplicate
                return callback(duplicates[0])
            }

            //  Else, we get a different model based on whether this has letter or numbers
            const model = hasLetters
                ? this.handleLetters(searchText)
                : this.handleNumbers(searchText)

            //  Handle it here
            this.popNewContact(model)
        },
        //  Handles our search text string if there are only letters by parsing names from query
        handleLetters(searchText: string): Person {
            //  Create a new model
            const model = this.newPerson()

            //  Try splitting by space
            const text = searchText.split(' ')

            //  Set the first name to the first part of the search text
            const given = text[0]

            //  Grab the length of our text
            const len = text.length

            //  Set the last name to the last part of the search text (if it exists)
            const family = len > 1 ? text.slice(1, len).join(' ') : null

            //  Update the new person model with the new first/last names
            model.name_given = given
            model.name_family = family

            //  Return our model
            return model
        },
        //  Handles our search text string if there are numbers by parsing phone number from query
        handleNumbers(searchText: string): Person {
            //  Create our model as a new person
            const model = this.newPerson()

            //  Get a phone from this search text
            const phone = this.getContactPhone(searchText)

            //  Set the free form number to the search text
            model.phone_default.free_form_number = phone

            //  Return our model
            return model
        }
    }
}